import React from 'react';
import clsx from 'clsx';
import { Await, TransitionDiv } from '@oysterjs/ui/async';
import {
  Form,
  Link,
  NavLink,
  useActionData,
  useLoaderData,
  useNavigate,
  useNavigation,
  useOutlet,
  useSubmit
} from 'react-router-dom';
import {
  AccountBusinessPolicyFieldsFragment,
  AccountFieldsFragment,
  AccountServiceRequestFieldsFragment,
  BusinessPolicyFieldsFragment,
  CreateServiceRequestInput,
  CreateServiceRequestMutation,
  QuestionRequestType,
  ServiceRequestFieldsFragment,
  ServiceRequestState,
  ServiceRequestType
} from '../api/graphql';
import { MountableSlideout } from '@oysterjs/ui/slideout';
import { Select } from '@oysterjs/ui/select';
import { Description, ErrorMessage, Fieldset, Label } from '@oysterjs/ui/fieldset';
import { Field } from '@oysterjs/ui/fieldset';
import { FieldGroup } from '@oysterjs/ui/fieldset';
import { AnimatePresence } from 'motion/react';
import { Badge } from '@oysterjs/ui/badge';
import { Input } from '@oysterjs/ui/input';
import { AddressForm } from '@oysterjs/ui/address';
import { Button } from '@oysterjs/ui/button';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { getDisplayInsuranceType } from '../api/map';
import { Textarea } from '@oysterjs/ui/textarea';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { useFormActions } from '@oysterjs/core/graphql/form';
import { ValidationError } from '@oysterjs/core/graphql';
import { Radio, RadioField, RadioGroup } from '@oysterjs/ui/radio';
import { Spinner } from '@oysterjs/ui/spinner';

export const ServiceRequestsPage = () => {
  const outlet = useOutlet();
  const loader = useLoaderData() as
    | {
        data: Promise<{
          account: AccountFieldsFragment & AccountServiceRequestFieldsFragment;
        }>;
      }
    | undefined;

  return (
    <>
      <React.Suspense
        fallback={
          <TransitionDiv className="my-16 flex items-center justify-center">
            <div className="rounded-lg bg-white p-10 shadow">
              <Spinner size="size-10" color="neutral" />
            </div>
          </TransitionDiv>
        }
      >
        <Await resolve={loader?.data}>
          {(data) =>
            data && (
              <div className="rounded-lg bg-white px-5 py-6 shadow sm:px-6">
                <h2 className="text-xl font-semibold mt-0 mb-2">Your Service Requests</h2>
                <ServiceRequests serviceRequests={data.account.serviceRequests} />
              </div>
            )
          }
        </Await>
      </React.Suspense>
      <AnimatePresence mode="wait">{outlet && React.cloneElement(outlet, {})}</AnimatePresence>
    </>
  );
};

const titleCase = (s: string) =>
  s
    .split(/[\s_]/g)
    .map((part) => part[0].toUpperCase() + part.substring(1).toLowerCase())
    .join(' ');

export const ServiceRequests = (props: { serviceRequests: ServiceRequestFieldsFragment[] }) => {
  const serviceRequests = [...props.serviceRequests].sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  if (serviceRequests.length === 0) {
    return (
      <p className="text-sm text-neutral-500">
        No service requests found.{' '}
        <Link to="new" className="text-primary-600 hover:underline hover:text-primary-900">
          Create one.
        </Link>
      </p>
    );
  }

  return (
    <>
      <p className="text-sm text-neutral-500">
        <Link to="new" className="text-primary-600 hover:underline hover:text-primary-900">
          Create a new service request
        </Link>
      </p>
      <ul role="list" className="divide-y divide-gray-100 mt-4">
        {serviceRequests.map((serviceRequest) => (
          <li key={serviceRequest.id} className="py-3 first:pt-0 last:pb-0">
            <div>
              <div className="flex items-start gap-x-3">
                <NavLink
                  to={serviceRequest.id}
                  className="text-base/6 font-medium text-neutral-900 hover:underline"
                >
                  {titleCase(serviceRequest.type)}
                </NavLink>
                <Badge
                  // eslint-disable-next-line
                  // @ts-ignore
                  color={
                    {
                      [ServiceRequestState.Open]: 'amber',
                      [ServiceRequestState.Closed]: 'green'
                    }[serviceRequest.state] || 'neutral'
                  }
                >
                  {serviceRequest.state}
                </Badge>
              </div>
              <div className="flex items-center gap-x-2 text-neutral-500">
                <p className="whitespace-nowrap text-sm">
                  Created{' '}
                  <time dateTime={new Date(serviceRequest.createdAt).toISOString()}>
                    {new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(
                      new Date(serviceRequest.createdAt)
                    )}
                  </time>
                </p>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </>
  );
};

export const ServiceRequestPage = () => {
  const navigate = useNavigate();
  const loader = useLoaderData() as
    | {
        data: Promise<{
          serviceRequest: ServiceRequestFieldsFragment;
        }>;
      }
    | undefined;

  return (
    <MountableSlideout onClose={() => navigate(-1)} title="View Service Request">
      <React.Suspense fallback={<></>}>
        <Await resolve={loader?.data}>
          {(data) =>
            data?.serviceRequest && <ServiceRequest serviceRequest={data.serviceRequest} />
          }
        </Await>
      </React.Suspense>
    </MountableSlideout>
  );
};

const ServiceRequest = (props: { serviceRequest: ServiceRequestFieldsFragment }) => {
  const details = [
    {
      label: 'Request Details',
      value:
        props.serviceRequest.details ||
        props.serviceRequest.cancelPolicy?.reason ||
        props.serviceRequest.question?.question
    },
    {
      label: 'Additional Insured',
      value: props.serviceRequest.addAdditionalInsured?.name
    },
    {
      label: 'Closed At',
      value: props.serviceRequest.closedAt
        ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(
            new Date(props.serviceRequest.closedAt)
          )
        : null
    },
    {
      label: 'Updated At',
      value: props.serviceRequest.updatedAt
        ? new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(
            new Date(props.serviceRequest.updatedAt)
          )
        : null
    }
  ].filter((d): d is { label: string; value: string } => !!d.value);

  return (
    <>
      <div className="flex items-center justify-between gap-x-2">
        <h2 className="text-xl font-semibold m-0">{titleCase(props.serviceRequest.type)}</h2>
        <Badge
          // eslint-disable-next-line
          // @ts-ignore
          color={
            {
              [ServiceRequestState.Open]: 'amber',
              [ServiceRequestState.Closed]: 'green'
            }[props.serviceRequest.state] || 'neutral'
          }
          className="self-center"
        >
          {props.serviceRequest.state}
        </Badge>
      </div>
      <p className="text-sm text-neutral-500">
        Created{' '}
        <time dateTime={new Date(props.serviceRequest.createdAt).toISOString()}>
          {new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(
            new Date(props.serviceRequest.createdAt)
          )}
        </time>
      </p>
      <dl className="divide-y divide-neutral-100 mt-4">
        {details.map((detail) => (
          <div key={detail.label} className="py-3 first:pt-0 last:pb-0">
            <dt className="text-sm font-medium text-neutral-500">{detail.label}</dt>
            <dd className="text-sm text-neutral-900">{detail.value}</dd>
          </div>
        ))}
      </dl>
    </>
  );
};

export const CreateServiceRequestPage = () => {
  const navigate = useNavigate();
  const loader = useLoaderData() as
    | {
        data: Promise<{
          account: AccountFieldsFragment & AccountBusinessPolicyFieldsFragment;
        }>;
      }
    | undefined;

  return (
    <MountableSlideout onClose={() => navigate(-1)} title="Create Service Request">
      <React.Suspense fallback={<></>}>
        <Await resolve={loader?.data}>
          {(data) =>
            data?.account && <CreateServiceRequest policies={data.account.businessPolicies} />
          }
        </Await>
      </React.Suspense>
    </MountableSlideout>
  );
};

export const CreateServiceRequest = (props: { policies: BusinessPolicyFieldsFragment[] }) => {
  const navigation = useNavigation();
  const navigate = useNavigate();
  const submit = useSubmit();
  const action = useActionData() as ActionResult<CreateServiceRequestMutation>;

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<CreateServiceRequestInput>();
  const { updateForm, updateFormMulti, getValidationError } = useFormActions(
    setForm,
    setValidationErrors
  );

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  React.useEffect(() => {
    if (action?.data?.createServiceRequest?.id) {
      navigate(-1);
    }
  }, [JSON.stringify(action)]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (!form) {
      return;
    }

    e.preventDefault();
    submit(
      { ...form, accountId: window.sessionStorage.getItem('_accountid') || '' },
      { method: 'POST', encType: 'application/json' }
    );
  };

  return (
    <Form className={clsx('flex-1')} onSubmit={handleSubmit}>
      <Fieldset>
        <FieldGroup>
          <Field>
            <Label>Choose a category</Label>
            <Select
              onChange={(e) => {
                const type = e.currentTarget.value as ServiceRequestType;
                updateForm(['type'], (prev) => ({
                  ...prev,
                  type,
                  details: '',
                  accountId: window.sessionStorage.getItem('accountid') || ''
                }));
              }}
              value={form?.type || ''}
              invalid={!!getValidationError(validationErrors, ['type'])}
            >
              <option value="" disabled>
                Select a category
              </option>
              <option value={ServiceRequestType.CreateCertificate}>
                Get a certificate of insurance
              </option>
              <option value={ServiceRequestType.AddAdditionalInsured}>
                Add an additional insured
              </option>
              <option value={ServiceRequestType.EndorsePolicy}>Modify your coverage</option>
              <option value={ServiceRequestType.CancelPolicy}>Cancel your policy</option>
              <option value={ServiceRequestType.Question}>Ask a question</option>
              <option value={ServiceRequestType.Other}>Something else</option>
            </Select>
            <ErrorMessage>{getValidationError(validationErrors, ['type'])}</ErrorMessage>
          </Field>
        </FieldGroup>

        {form?.type === ServiceRequestType.AddAdditionalInsured && (
          <>
            <FieldGroup>
              <Field>
                <Label>Name</Label>
                <Description>The name of the additional insured</Description>
                <Input
                  value={form?.addAdditionalInsured?.name || ''}
                  placeholder="ACME Inc."
                  onChange={(e) => {
                    const name = e.currentTarget.value;
                    updateForm(['addAdditionalInsured', 'name'], (prev) =>
                      !prev
                        ? undefined
                        : {
                            ...prev,
                            addAdditionalInsured: {
                              address: prev?.addAdditionalInsured?.address || {
                                line1: '',
                                city: '',
                                zone: '',
                                postalCode: ''
                              },
                              name,
                              relationship: prev?.addAdditionalInsured?.relationship || ''
                            }
                          }
                    );
                  }}
                  invalid={!!getValidationError(validationErrors, ['addAdditionalInsured', 'name'])}
                />
                <ErrorMessage>
                  {getValidationError(validationErrors, ['addAdditionalInsured', 'name'])}
                </ErrorMessage>
              </Field>
            </FieldGroup>
            <AddressForm
              addressTitle="Address"
              addressDescription="Enter the address of the additional insured."
              initialValue={{
                line1: form?.addAdditionalInsured?.address?.line1 || '',
                line2: form?.addAdditionalInsured?.address?.line2 || '',
                city: form?.addAdditionalInsured?.address?.city || '',
                zone: form?.addAdditionalInsured?.address?.zone || '',
                postalCode: form?.addAdditionalInsured?.address?.postalCode || ''
              }}
              onChange={(update) =>
                updateFormMulti(
                  [
                    ['addAdditionalInsured', 'address', 'line1'],
                    ['addAdditionalInsured', 'address', 'line2'],
                    ['addAdditionalInsured', 'address', 'city'],
                    ['addAdditionalInsured', 'address', 'zone'],
                    ['addAdditionalInsured', 'address', 'postalCode']
                  ],
                  (prev) =>
                    !prev
                      ? undefined
                      : {
                          ...prev,
                          addAdditionalInsured: {
                            address: update(
                              prev?.addAdditionalInsured?.address || {
                                line1: '',
                                city: '',
                                zone: '',
                                postalCode: ''
                              }
                            ),
                            name: prev?.addAdditionalInsured?.name || '',
                            relationship: prev?.addAdditionalInsured?.relationship || ''
                          }
                        }
                )
              }
              validationErrors={{
                line1: getValidationError(validationErrors, [
                  'addAdditionalInsured',
                  'address',
                  'line1'
                ]),
                line2: getValidationError(validationErrors, [
                  'addAdditionalInsured',
                  'address',
                  'line2'
                ]),
                city: getValidationError(validationErrors, [
                  'addAdditionalInsured',
                  'address',
                  'city'
                ]),
                zone: getValidationError(validationErrors, [
                  'addAdditionalInsured',
                  'address',
                  'zone'
                ]),
                postalCode: getValidationError(validationErrors, [
                  'addAdditionalInsured',
                  'address',
                  'postalCode'
                ])
              }}
            />
            <FieldGroup>
              <Field>
                <Label>Relationship</Label>
                <Description>
                  The relationship of the additional insured to the policyholder.
                </Description>
                <Input
                  value={form?.addAdditionalInsured?.relationship || ''}
                  placeholder="Contractor, Vendor, Landlord, etc."
                  onChange={(e) => {
                    const relationship = e.currentTarget.value;
                    updateForm(['addAdditionalInsured', 'relationship'], (prev) =>
                      !prev
                        ? undefined
                        : {
                            ...prev,
                            addAdditionalInsured: {
                              address: prev?.addAdditionalInsured?.address || {
                                line1: '',
                                city: '',
                                zone: '',
                                postalCode: ''
                              },
                              name: prev?.addAdditionalInsured?.name || '',
                              relationship
                            }
                          }
                    );
                  }}
                  invalid={
                    !!getValidationError(validationErrors, ['addAdditionalInsured', 'relationship'])
                  }
                />
                <ErrorMessage>
                  {getValidationError(validationErrors, ['addAdditionalInsured', 'relationship'])}
                </ErrorMessage>
              </Field>
            </FieldGroup>
          </>
        )}

        {form?.type === ServiceRequestType.CancelPolicy && (
          <>
            <FieldGroup>
              <Field>
                <Label>Select a policy</Label>
                <Description>Select the policy you want to cancel.</Description>
                <Select
                  value={form?.cancelPolicy?.policyId || ''}
                  onChange={(e) => {
                    const policyId = e.currentTarget.value;
                    updateForm(['cancelPolicy', 'policyId'], (prev) =>
                      !prev
                        ? undefined
                        : {
                            ...prev,
                            cancelPolicy: {
                              policyId,
                              reason: prev.cancelPolicy?.reason || ''
                            }
                          }
                    );
                  }}
                  invalid={!!getValidationError(validationErrors, ['cancelPolicy', 'policyId'])}
                >
                  <option value="" disabled>
                    Select a policy
                  </option>
                  {props.policies.map((p) => (
                    <option key={p.id} value={p.id}>
                      {getDisplayInsuranceType(p.type)} ({p.carrier.name} &middot;{' '}
                      {p.policyNumber || 'N/A'})
                    </option>
                  ))}
                </Select>
                <ErrorMessage>
                  {getValidationError(validationErrors, ['cancelPolicy', 'policyId'])}
                </ErrorMessage>
              </Field>
              <Field>
                <Label>Reason for cancellation</Label>
                <Description>Provide a reason for why you want to cancel your policy.</Description>
                <Input
                  value={form?.cancelPolicy?.reason || ''}
                  onChange={(e) => {
                    const reason = e.currentTarget.value;
                    updateForm(['cancelPolicy', 'reason'], (prev) =>
                      !prev
                        ? undefined
                        : {
                            ...prev,
                            cancelPolicy: {
                              reason,
                              policyId: prev.cancelPolicy?.policyId || ''
                            }
                          }
                    );
                  }}
                  invalid={!!getValidationError(validationErrors, ['cancelPolicy', 'reason'])}
                />
                <ErrorMessage>
                  {getValidationError(validationErrors, ['cancelPolicy', 'reason'])}
                </ErrorMessage>
              </Field>
            </FieldGroup>
          </>
        )}

        {form?.type === ServiceRequestType.Question && (
          <>
            <RadioGroup
              value={form?.question?.type || ''}
              onChange={(val) => {
                updateForm(['question', 'type'], (prev) =>
                  !prev
                    ? undefined
                    : {
                        ...prev,
                        question: {
                          type: val as QuestionRequestType,
                          policyId: null,
                          question: prev.question?.question || ''
                        }
                      }
                );
              }}
            >
              <RadioField>
                <Radio value={QuestionRequestType.Policy} color="primary" />
                <Label>Question about a policy</Label>
                <Description>Ask a question about a specific policy.</Description>
              </RadioField>
              <RadioField>
                <Radio value={QuestionRequestType.Other} color="primary" />
                <Label>Something else</Label>
                <Description>Ask a question about something else not listed above.</Description>
              </RadioField>
              <ErrorMessage>
                {getValidationError(validationErrors, ['question', 'type'])}
              </ErrorMessage>
            </RadioGroup>

            <FieldGroup>
              {form?.question?.type === QuestionRequestType.Policy && (
                <Field>
                  <Label>Policy</Label>
                  <Description>Select the policy you want to ask about.</Description>
                  <Select
                    value={form?.question?.policyId || ''}
                    onChange={(e) => {
                      const policyId = e.currentTarget.value;
                      updateForm(['question', 'policyId'], (prev) =>
                        !prev
                          ? undefined
                          : {
                              ...prev,
                              question: {
                                type: QuestionRequestType.Policy,
                                question: prev.question?.question || '',
                                policyId
                              }
                            }
                      );
                    }}
                    invalid={!!getValidationError(validationErrors, ['question', 'policyId'])}
                  >
                    <option value="" disabled>
                      Select a policy
                    </option>
                    {props.policies.map((p) => (
                      <option key={p.id} value={p.id}>
                        {getDisplayInsuranceType(p.type)} ({p.carrier.name} &middot;{' '}
                        {p.policyNumber || 'N/A'})
                      </option>
                    ))}
                  </Select>
                  <ErrorMessage>
                    {getValidationError(validationErrors, ['question', 'policyId'])}
                  </ErrorMessage>
                </Field>
              )}
              <Field>
                <Label>Question</Label>
                <Description>
                  Provide the question that you want to ask. Our team will respond promptly.
                </Description>
                <Textarea
                  value={form?.question?.question || ''}
                  onChange={(e) => {
                    const question = e.currentTarget.value;
                    updateForm(['question', 'question'], (prev) =>
                      !prev
                        ? undefined
                        : {
                            ...prev,
                            question: {
                              type: prev.question?.type || QuestionRequestType.Other,
                              question,
                              policyId: prev.question?.policyId || null
                            }
                          }
                    );
                  }}
                  invalid={!!getValidationError(validationErrors, ['question', 'question'])}
                  rows={4}
                />
                <ErrorMessage>
                  {getValidationError(validationErrors, ['question', 'question'])}
                </ErrorMessage>
              </Field>
            </FieldGroup>
          </>
        )}

        {!!form &&
          [
            ServiceRequestType.AddAdditionalInsured,
            ServiceRequestType.EndorsePolicy,
            ServiceRequestType.Other
          ].includes(form.type) && (
            <FieldGroup>
              <Field>
                <Label>Details</Label>
                <Description>
                  Provide any additional details that will help us process your request.
                </Description>
                <Textarea
                  value={form?.details || ''}
                  onChange={(e) => {
                    const details = e.currentTarget.value;
                    updateForm(['type'], (prev) =>
                      !prev
                        ? undefined
                        : {
                            ...prev,
                            details
                          }
                    );
                  }}
                  invalid={!!getValidationError(validationErrors, ['details'])}
                  rows={4}
                />
                <ErrorMessage>{getValidationError(validationErrors, ['details'])}</ErrorMessage>
              </Field>
            </FieldGroup>
          )}
      </Fieldset>

      {form?.type === ServiceRequestType.CreateCertificate && (
        <div className="mt-6 bg-neutral-100 rounded-lg p-8">
          <h3 className="m-0 text-lg font-semibold">Generate a certificate</h3>
          <p className="text-sm">
            You can generate certificates of insurance right from the dashboard, with just the
            certificate holder's name and address.
          </p>
          <Button color="sky" className="mt-4" href="/certificates/new">
            Generate a COI <ArrowRightIcon className="w-4 h-4" />
          </Button>
        </div>
      )}

      {!!form && form.type !== ServiceRequestType.CreateCertificate && (
        <Button
          color="sky"
          className="mt-6"
          type="submit"
          loading={navigation.state === 'submitting'}
          disabled={!form?.type}
        >
          Submit request <ArrowRightIcon className="w-4 h-4" />
        </Button>
      )}
    </Form>
  );
};
